import { useState, useRef } from 'react';

// ── Formulaire d'activation TOTP (scan QR + code de confirmation) ──
// Utilisé à la fois pour l'auto-activation (Paramètres / Mon profil)
// et pour l'enrôlement forcé au login quand l'organisation impose la 2FA.
// props :
//   qrDataUrl, secret — fournis par /totp/setup(-challenge)
//   onVerify(code) — appelé quand les 6 chiffres sont saisis
//   loading, error
//   showRemember, remember, setRemember — optionnel (contexte login forcé)
//   dark — palette sombre (écrans de login) vs. thème de l'app (Paramètres)
export default function TotpEnrollForm({
  qrDataUrl, secret, onVerify, loading, error,
  showRemember, remember, setRemember, dark,
}) {
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [copied, setCopied] = useState(false);
  const inputRefs = useRef([]);
  const submittedRef = useRef(false);

  const colors = dark
    ? { bg:'#0a0b0f', card:'#13141a', border:'#2a2b35', text:'#fff', muted:'#888', accent:'#7c5cfc' }
    : { bg:'transparent', card:'var(--bg-elevated)', border:'var(--border)', text:'var(--text-1)',
        muted:'var(--text-3)', accent:'var(--accent-light)' };

  const submitCode = (code) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    onVerify(code);
  };

  const handleChange = (i, val) => {
    const digitsOnly = val.replace(/[^0-9]/g, '');
    // Auto-remplissage (Trousseau iCloud / clavier QuickType / gestionnaires de mots
    // de passe) : le code entier peut arriver d'un coup dans une seule case via un
    // événement "input" plutôt qu'un vrai "paste". On le redistribue comme un collage.
    if (digitsOnly.length > 1) {
      const chars = digitsOnly.slice(0, 6).split('');
      const next = [...digits];
      for (let j = 0; j < chars.length && i + j < 6; j++) next[i + j] = chars[j];
      setDigits(next);
      const lastFilled = Math.min(i + chars.length, 6) - 1;
      inputRefs.current[Math.min(lastFilled + 1, 5)]?.focus();
      const joined = next.join('');
      if (joined.length === 6 && next.every(d => d !== '')) submitCode(joined);
      return;
    }
    const v = digitsOnly;
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v && i < 5) inputRefs.current[i + 1]?.focus();
    const joined = next.join('');
    if (joined.length === 6 && next.every(d => d !== '')) submitCode(joined);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) inputRefs.current[i - 1]?.focus();
  };

  const copySecret = () => {
    navigator.clipboard?.writeText(secret).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };

  return (
    <div style={{ textAlign:'center' }}>
      <p style={{ color:colors.muted, fontSize:13, marginBottom:16, lineHeight:1.5 }}>
        Scannez ce QR code avec Google Authenticator, Authy ou toute application compatible TOTP.
      </p>

      {qrDataUrl && (
        <div style={{ display:'inline-block', padding:12, background:'#fff', borderRadius:10, marginBottom:12 }}>
          <img src={qrDataUrl} alt="QR code 2FA" width={160} height={160}/>
        </div>
      )}

      {secret && (
        <div onClick={copySecret} title="Cliquer pour copier"
          style={{ cursor:'pointer', fontFamily:'monospace', fontSize:13, letterSpacing:'0.05em',
            color:colors.accent, background:colors.card, border:`1px solid ${colors.border}`,
            borderRadius:8, padding:'8px 12px', marginBottom:20, wordBreak:'break-all' }}>
          {secret} {copied && <span style={{ color:colors.muted }}>· copié !</span>}
        </div>
      )}

      <p style={{ color:colors.muted, fontSize:13, marginBottom:10 }}>
        Puis entrez le code à 6 chiffres généré par l'application :
      </p>

      <div style={{ display:'flex', gap:8, justifyContent:'center', marginBottom:16 }}>
        {digits.map((d, i) => (
          <input key={i} ref={el => inputRefs.current[i] = el}
            type="text" inputMode="numeric" maxLength={i === 0 ? 6 : 1} value={d}
            disabled={loading}
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            onChange={e => handleChange(i, e.target.value)}
            onKeyDown={e => handleKeyDown(i, e)}
            onFocus={e => e.target.select()}
            style={{ width:44, height:52, textAlign:'center', fontSize:20, fontWeight:700,
              background:colors.card, border:`1px solid ${colors.border}`, borderRadius:10,
              color:colors.text, outline:'none' }}/>
        ))}
      </div>

      {showRemember && (
        <label style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'8px 16px',
          background:colors.card, border:`1px solid ${colors.border}`, borderRadius:8, cursor:'pointer',
          fontSize:13, color:colors.muted, marginBottom:12 }}>
          <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)}/>
          Se souvenir de cet appareil
        </label>
      )}

      {loading && <div style={{ color:colors.muted, fontSize:12, marginBottom:8 }}>Vérification...</div>}
      {error && <div style={{ color:'#ff4d6d', fontSize:13, marginBottom:8 }}>{error}</div>}
    </div>
  );
}
